//VehicleFormAdd
import {
  React,
  useState,
  useEffect,
  useAuth,
  useRef,
  useClientInfo,
  axiosInstance,
  SnackbarAlert,
  Box,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Switch,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Button,
  Typography,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  CategoryDropdown,
  DocumentManager,
  TariffManager,
} from "../../utils/commonImports";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */
const AC_TYPES = ["AC", "Non-AC"];
const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "EV"];
const OWNERSHIP_TYPES = ["Own", "Attach", "Vendor"];
const REGISTERED_TREATMENT_IDS = [21, 22, 99]; // or your correct IDs here
const TABS = ["Info", "Owner/Driver", "Documents", "GPRS", "Tariff"];

const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  border: "#e2e8f0",
  surface: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
};

// Dense input styling shared by every field
const fieldSx = {
  "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" },
  "& .MuiInputBase-input": { py: "7px" },
  "& .MuiSelect-select": { py: "7px !important" },
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiInputLabel-shrink": { fontSize: 13 },
  "& .MuiFormHelperText-root": { fontSize: 11, mx: 0 },
};

const labelSx = { "& .MuiFormControlLabel-label": { fontSize: 13 } };

const btnBase = {
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "6px",
  boxShadow: "none",
  minWidth: 0,
  px: 2,
  py: 0.6,
};

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */
const Card = ({ title, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "8px",
      backgroundColor: "#fff",
      ...sx,
    }}
  >
    {title && (
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.surface,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {title}
        </Typography>
      </Box>
    )}
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const Grid = ({ cols = 3, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1.5,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: `repeat(${cols}, 1fr)`,
      },
      alignItems: "start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Text input / select with consistent dense styling
const Field = ({ options, sx, ...props }) => (
  <TextField
    size="small"
    fullWidth
    select={!!options}
    sx={{ ...fieldSx, ...sx }}
    {...props}
  >
    {options &&
      options.map((o) => (
        <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
          {o}
        </MenuItem>
      ))}
  </TextField>
);

const Toggle = ({ label, checked, onChange, name }) => (
  <FormControlLabel
    sx={{ m: 0, ...labelSx }}
    control={
      <Switch
        size="small"
        color="primary"
        name={name}
        checked={!!checked}
        onChange={onChange}
      />
    }
    label={label}
  />
);

// Keeps panels mounted (so DocumentManager / TariffManager keep their state)
const Panel = ({ active, children }) => (
  <Box sx={{ display: active ? "block" : "none" }}>{children}</Box>
);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function VehicleFormAdd({ editData, onCancel, onSuccess }) {
  const { clientId } = useClientInfo();
  const { token } = useAuth();

  const [vehicleGroupOptions, setVehicleGroupOptions] = useState([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);

  // Document states
  const [documents, setDocuments] = useState([]);
  const [deletedDocumentIds, setDeletedDocumentIds] = useState([]);

  const [vehicleTariffs, setVehicleTariffs] = useState([]);
  const tariffManagerRef = useRef(null);

  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    vehicle_number: "",
    vehicle_group_id: "",
    vehicle_group: "",
    vehicle_type_id: "",
    vehicle_type_name: "",
    vehicle_ac_type: "",
    vehicle_fuel_type: "",
    vehicle_ownership: "",
    vehicle_location: "",
    active: "Yes", // "Yes" or "No"
    vehicle_dol: "",
    vehicle_left_reason: "",
    vehicle_doj: "",
    vehicle_mileage: "",
    vehicle_start_km: "",
    vehicle_run_km_reading: "",
    vehicle_comm: "",
    tcs_flag: "No", // "Yes" or "No"
    vendor_tds_per: "",
    vehicle_color: "",
    vehicle_model: "",
    vehicle_engine_no: "",
    vehicle_chassis_no: "",
    vehicle_warning_days: "",
    vehicle_driver_id: "",
    vehicle_driver_name: "",
    vehicle_driver_mobile_no: "",
    vehicle_owner_name: "",
    vehicle_owner_mobile: "",
    vehicle_owner_address: "",
    vehicle_owner_pan_no: "",
    vehicle_owner_pan_upload: null,
    vehicle_owner_name_in_pan: "",
    vehicle_owner_bank_ac_no: "",
    vehicle_owner_bank_ac_name: "",
    vehicle_owner_bank_name: "",
    vehicle_owner_bank_branch: "",
    vehicle_owner_bank_ifsc_code: "",
    gps_provider_name: "",
    gps_api_url: "",
    gps_api_key: "",
    gps_device_no: "",
    vehicle_tax_preference: "",
    vehicle_tax_exempted_reason: "",
    // GST fields (field names kept exactly as used by the backend)
    vehcile_gst_treatment: "",
    vehicle_gst_no: "",
    vehile_place_of_supply: "",
    vehicle_gst_parking_toll_permit_flg: false,
    vehicle_gst_batta_flg: false,
    vehicle_gst_others_flg: false,
    client_id: "",
  });

  /* ---------------------------- Effects ---------------------------- */
  // Load dropdown options (categoryid converted to string)
  useEffect(() => {
    const load = (type, setter) =>
      fetch(`/api/components/category?type=${type}`)
        .then((res) => res.json())
        .then((data) =>
          setter(data.map((o) => ({ ...o, categoryid: String(o.categoryid) }))),
        )
        .catch(console.error);

    load("VehicleGroup", setVehicleGroupOptions);
    load("VehicleType", setVehicleTypeOptions);
  }, []);

  // Load edit data (IDs converted to strings)
  useEffect(() => {
    if (editData && !isFormInitialized) {
      setFormData((prev) => ({
        ...prev,
        vehicle_group_id: editData.vehicle_group_id
          ? String(editData.vehicle_group_id)
          : "",
        vehicle_type_id: editData.vehicle_type_id
          ? String(editData.vehicle_type_id)
          : "",
        vehicle_number: editData.vehicle_number || "",
        vehicle_group: editData.vehicle_group || "",
        vehicle_type_name: editData.vehicle_type_name || "",
        vehicle_ac_type: editData.vehicle_ac_type || "",
        vehicle_fuel_type: editData.vehicle_fuel_type || "",
        vehicle_ownership: editData.vehicle_ownership || "",
        vehicle_location: editData.vehicle_location || "",
        active: editData.active || "Yes",
        vehicle_dol: editData.vehicle_dol?.slice(0, 10) || "",
        vehicle_left_reason: editData.vehicle_left_reason || "",
        vehicle_doj: editData.vehicle_doj?.slice(0, 10) || "",
        vehicle_mileage: editData.vehicle_mileage || "",
        vehicle_start_km: editData.vehicle_start_km || "",
        vehicle_run_km_reading: editData.vehicle_run_km_reading || "",
        vehicle_comm: editData.vehicle_comm || "",
        tcs_flag: editData.tcs_flag || "No",
        vendor_tds_per: editData.vendor_tds_per || "",
        vehicle_color: editData.vehicle_color || "",
        vehicle_model: editData.vehicle_model || "",
        vehicle_engine_no: editData.vehicle_engine_no || "",
        vehicle_chassis_no: editData.vehicle_chassis_no || "",
        vehicle_warning_days: editData.vehicle_warning_days || "",
        vehicle_driver_id: editData.vehicle_driver_id || "",
        vehicle_driver_name: editData.vehicle_driver_name || "",
        vehicle_driver_mobile_no: editData.vehicle_driver_mobile_no || "",
        vehicle_owner_name: editData.vehicle_owner_name || "",
        vehicle_owner_mobile: editData.vehicle_owner_mobile || "",
        vehicle_owner_address: editData.vehicle_owner_address || "",
        vehicle_owner_pan_no: editData.vehicle_owner_pan_no || "",
        vehicle_owner_pan_upload: null,
        vehicle_owner_name_in_pan: editData.vehicle_owner_name_in_pan || "",
        vehicle_owner_bank_ac_no: editData.vehicle_owner_bank_ac_no || "",
        vehicle_owner_bank_ac_name: editData.vehicle_owner_bank_ac_name || "",
        vehicle_owner_bank_name: editData.vehicle_owner_bank_name || "",
        vehicle_owner_bank_branch: editData.vehicle_owner_bank_branch || "",
        vehicle_owner_bank_ifsc_code:
          editData.vehicle_owner_bank_ifsc_code || "",
        gps_api_url: editData.gps_api_url || "",
        gps_api_key: editData.gps_api_key || "",
        gps_device_no: editData.gps_device_no || "",
        vehicle_tax_preference: editData.vehicle_tax_preference || "",
        vehicle_tax_exempted_reason: editData.vehicle_tax_exempted_reason || "",
        // GST fields
        vehcile_gst_treatment: editData.vehcile_gst_treatment ?? "",
        vehicle_gst_no: editData.vehicle_gst_no || "",
        vehile_place_of_supply: editData.vehile_place_of_supply ?? "",
        vehicle_gst_parking_toll_permit_flg:
          !!editData.vehicle_gst_parking_toll_permit_flg,
        vehicle_gst_batta_flg: !!editData.vehicle_gst_batta_flg,
        vehicle_gst_others_flg: !!editData.vehicle_gst_others_flg,
      }));
      setIsFormInitialized(true);
    }
  }, [editData, isFormInitialized]);

  useEffect(() => {
    if (!formData.client_id && clientId) {
      setFormData((prev) => ({ ...prev, client_id: clientId }));
    }
  }, [clientId, formData.client_id]);

  /* --------------------------- Handlers ---------------------------- */
  const validate = () => {
    const e = {};
    if (!formData.vehicle_number.trim())
      e.vehicle_number = "Vehicle number is required";
    if (!formData.vehicle_group_id)
      e.vehicle_group_id = "Vehicle group is required";
    if (!formData.vehicle_type_id)
      e.vehicle_type_id = "Vehicle type is required";
    if (!formData.vehicle_fuel_type)
      e.vehicle_fuel_type = "Fuel type is required";
    if (!formData.vehicle_ac_type) e.vehicle_ac_type = "AC type is required";
    if (!formData.vehicle_ownership)
      e.vehicle_ownership = "Ownership is required";
    if (!formData.vehicle_location.trim())
      e.vehicle_location = "Location is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const setFlag = (name) => (e) =>
    setFormData((prev) => ({ ...prev, [name]: e.target.checked }));

  const setYesNo = (name) => (e) =>
    setFormData((prev) => ({
      ...prev,
      [name]: e.target.checked ? "Yes" : "No",
    }));

  const handleVehicleGroupChange = (event) => {
    const val = event.target.value;
    const selected =
      vehicleGroupOptions.find((o) => String(o.categoryid) === String(val)) ||
      null;
    setFormData((prev) => ({
      ...prev,
      client_id: clientId || prev.client_id,
      vehicle_group_id: selected ? selected.categoryid : "",
      vehicle_group: selected ? selected.categoryname : "",
    }));
  };

  const handleVehicleTypeChange = (event) => {
    const val = event.target.value;
    const selected =
      vehicleTypeOptions.find((o) => String(o.categoryid) === String(val)) ||
      null;
    setFormData((prev) => ({
      ...prev,
      vehicle_type_id: selected ? selected.categoryid : "",
      vehicle_type_name: selected ? selected.categoryname : "",
    }));
  };

  const handleDocumentsChange = (docs, deletedIds) => {
    setDocuments(docs);
    setDeletedDocumentIds(deletedIds);
  };

  const handleTariffsChange = (updatedTariffs) =>
    setVehicleTariffs(updatedTariffs);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      SnackbarAlert("Please fill in the required fields", "error");
      return;
    }

    setSaving(true);
    try {
      const isEditMode = !!editData?.vehicle_id;
      let vehicleId = editData?.vehicle_id;

      // Prepare vehicle payload
      const vehiclePayload = { ...formData, client_id: clientId };
      delete vehiclePayload.documents;
      delete vehiclePayload.deletedDocumentIds;

      // Save or update vehicle data
      if (isEditMode) {
        await axiosInstance.put(`/api/vehicles/${vehicleId}`, vehiclePayload);
      } else {
        const { data } = await axiosInstance.post(
          `/api/vehicles`,
          vehiclePayload,
        );
        vehicleId = data.vehicle_id;
      }

      // Delete removed documents if any
      if (deletedDocumentIds?.length > 0) {
        await axiosInstance.post("/api/documents/delete", {
          documentIds: deletedDocumentIds,
        });
      }

      // Upload vehicle documents separately
      if (documents.length > 0) {
        const formDataPayload = new FormData();
        formDataPayload.append("documentsMeta", JSON.stringify(documents));
        documents.forEach((doc) => {
          if (doc.file instanceof File) {
            formDataPayload.append("documentFiles", doc.file);
          }
        });
        formDataPayload.append("client_id", clientId);
        await axiosInstance.post(
          `/api/documents/vehicle/${vehicleId}/batch-save/${clientId}`,
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      }

      // Save tariffs if vehicle ID available and tariffs exist
      if (vehicleTariffs.length > 0 && vehicleId) {
        await axiosInstance.post(`/api/tariffs/sync/vehicle/${vehicleId}`, {
          tariffs: vehicleTariffs,
          client_id: clientId,
        });
      }

      SnackbarAlert(
        isEditMode
          ? "Vehicle updated successfully"
          : "Vehicle created successfully",
        "success",
      );
      onSuccess?.();
    } catch (error) {
      const status = error.response?.status;
      const responseData = error.response?.data;

      console.error(
        "Error saving vehicle, documents, or tariffs:",
        responseData || error.message,
      );

      const message =
        responseData?.message ||
        responseData?.error ||
        error.message ||
        "Failed to save vehicle, documents, or tariffs.";

      SnackbarAlert(message, "error");
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------- Derived --------------------------- */
  const isRegistered = REGISTERED_TREATMENT_IDS.includes(
    Number(formData.vehcile_gst_treatment),
  );
  const isTaxExempt = formData.vehicle_tax_preference === "Tax Exempted";

  // Compact select with validation message
  const SelectField = ({ label, name, options }) => (
    <FormControl
      size="small"
      fullWidth
      required
      error={!!errors[name]}
      sx={fieldSx}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        name={name}
        value={formData[name]}
        onChange={handleChange}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
            {opt}
          </MenuItem>
        ))}
      </Select>
      {errors[name] && <FormHelperText>{errors[name]}</FormHelperText>}
    </FormControl>
  );

  /* ------------------------------ UI ------------------------------- */
  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1500,
          mt: 1,
          mx: "auto",
          bgcolor: "#fff",
          borderRadius: "10px",
          border: `1px solid ${C.border}`,
          overflowY: "auto",
          maxHeight: "calc(100vh - 80px)",
        }}
      >
        {/* ---------- Header ---------- */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${C.border}`,
            backgroundColor: C.surface,
          }}
        >
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.text }}>
            {editData ? "Edit vehicle" : "New vehicle"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Fields marked * are required
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* ---------- Basic details ---------- */}
          <Card title="Basic details">
            <Grid cols={4}>
              <Field
                required
                label="Vehicle number"
                name="vehicle_number"
                value={formData.vehicle_number}
                onChange={handleChange}
                error={!!errors.vehicle_number}
                helperText={errors.vehicle_number}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "12px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "12px",
                  },
                  "& .MuiInputBase-input": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    fontSize: "12px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "10px",
                  },
                }}
              />
              <CategoryDropdown
                clientId={clientId}
                token={token}
                categoryType="VehicleGroup"
                options={vehicleGroupOptions}
                value={formData.vehicle_group_id || ""}
                onChange={handleVehicleGroupChange}
                label="Vehicle Group"
                size="small"
                fullWidth
                error={!!errors.vehicle_group_id}
                helperText={errors.vehicle_group_id}
              />
              <CategoryDropdown
                clientId={clientId}
                token={token}
                categoryType="VehicleType"
                options={vehicleTypeOptions}
                value={formData.vehicle_type_id || ""}
                onChange={handleVehicleTypeChange}
                label="Vehicle Type"
                size="small"
                fullWidth
                error={!!errors.vehicle_type_id}
                helperText={errors.vehicle_type_id}
              />
              <SelectField
                label="Fuel type"
                name="vehicle_fuel_type"
                options={FUEL_TYPES}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 48,
                    fontSize: "12px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "12px",
                  },
                  "& .MuiSelect-select": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
              />

              <SelectField
                label="AC type"
                name="vehicle_ac_type"
                options={AC_TYPES}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 48,
                    fontSize: "12px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "12px",
                  },
                  "& .MuiSelect-select": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
              />

              <SelectField
                label="Ownership"
                name="vehicle_ownership"
                options={OWNERSHIP_TYPES}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 56,
                    fontSize: "12px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "12px",
                  },
                  "& .MuiSelect-select": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
              />
              <CategoryDropdown
                clientId={clientId}
                token={token}
                categoryType="Location"
                value={formData.vehicle_location || ""}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    vehicle_location: value,
                  }))
                }
                label="Location"
                size="small"
                fullWidth
                error={!!errors.vehicle_location}
                helperText={errors.vehicle_location}
              />
              <Toggle
                label="Active"
                name="active"
                checked={formData.active === "Yes"}
                onChange={setYesNo("active")}
              />
            </Grid>
          </Card>

          {/* ---------- Tabs ---------- */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            sx={{
              mt: 3,
              mb: 2,
              minHeight: 38,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            {TABS.map((label) => (
              <Tab
                key={label}
                label={
                  label === "Documents" && documents.length > 0
                    ? `${label} (${documents.length})`
                    : label === "Tariff" && vehicleTariffs.length > 0
                      ? `${label} (${vehicleTariffs.length})`
                      : label
                }
                sx={{
                  minHeight: 38,
                  py: 0.5,
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: "none",
                }}
              />
            ))}
          </Tabs>

          {/* ---------- INFO ---------- */}
          <Panel active={activeTab === 0}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
                alignItems: "start",
              }}
            >
              <Card title="Dates & usage">
                <Grid cols={1}>
                  <Field
                    label="Date of joining"
                    type="date"
                    name="vehicle_doj"
                    value={formData.vehicle_doj}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Field
                    label="Date of leaving"
                    type="date"
                    name="vehicle_dol"
                    value={formData.vehicle_dol}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Field
                    label="Left reason"
                    name="vehicle_left_reason"
                    value={formData.vehicle_left_reason}
                    onChange={handleChange}
                  />
                  <Field
                    label="Mileage"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    name="vehicle_mileage"
                    value={formData.vehicle_mileage}
                    onChange={handleChange}
                  />
                  <Field
                    label="Start KM"
                    type="number"
                    inputProps={{ min: 0 }}
                    name="vehicle_start_km"
                    value={formData.vehicle_start_km}
                    onChange={handleChange}
                  />
                  <Field
                    label="Run KM reading"
                    type="number"
                    inputProps={{ min: 0 }}
                    name="vehicle_run_km_reading"
                    value={formData.vehicle_run_km_reading}
                    onChange={handleChange}
                  />
                </Grid>
              </Card>

              <Card title="Vehicle specs">
                <Grid cols={1}>
                  <Field
                    label="Color"
                    name="vehicle_color"
                    value={formData.vehicle_color}
                    onChange={handleChange}
                  />
                  <Field
                    label="Model (year)"
                    type="number"
                    inputProps={{ min: 0 }}
                    name="vehicle_model"
                    value={formData.vehicle_model}
                    onChange={handleChange}
                  />
                  <Field
                    label="Engine no."
                    name="vehicle_engine_no"
                    value={formData.vehicle_engine_no}
                    onChange={handleChange}
                  />
                  <Field
                    label="Chassis no."
                    name="vehicle_chassis_no"
                    value={formData.vehicle_chassis_no}
                    onChange={handleChange}
                  />
                  <Field
                    label="Commission"
                    type="number"
                    inputProps={{ min: 0 }}
                    name="vehicle_comm"
                    value={formData.vehicle_comm}
                    onChange={handleChange}
                  />
                  <Field
                    label="Warning days"
                    type="number"
                    inputProps={{ min: 0 }}
                    name="vehicle_warning_days"
                    value={formData.vehicle_warning_days}
                    onChange={handleChange}
                  />
                </Grid>
              </Card>

              <Card title="Tax & GST">
                <Grid cols={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      minHeight: 40,
                    }}
                  >
                    <Toggle
                      label="TCS"
                      name="tcs_flag"
                      checked={formData.tcs_flag === "Yes"}
                      onChange={setYesNo("tcs_flag")}
                    />
                    {formData.tcs_flag === "Yes" && (
                      <Field
                        label="Vendor TDS %"
                        type="number"
                        inputProps={{ min: 0, step: "0.01" }}
                        name="vendor_tds_per"
                        value={formData.vendor_tds_per}
                        onChange={handleChange}
                        sx={{ maxWidth: 150 }}
                      />
                    )}
                  </Box>

                  <FormControl component="fieldset" size="small">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                      }}
                    >
                      <FormLabel
                        component="legend"
                        sx={{ fontSize: 13, color: C.muted }}
                      >
                        Tax preference
                      </FormLabel>
                      <RadioGroup
                        row
                        name="vehicle_tax_preference"
                        value={formData.vehicle_tax_preference}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value="Taxable"
                          control={<Radio size="small" color="primary" />}
                          label="Taxable"
                          sx={labelSx}
                        />
                        <FormControlLabel
                          value="Tax Exempted"
                          control={<Radio size="small" color="primary" />}
                          label="Tax exempt"
                          sx={labelSx}
                        />
                      </RadioGroup>
                    </Box>
                  </FormControl>
                  <Field
                    label="Tax exempted reason"
                    name="vehicle_tax_exempted_reason"
                    value={formData.vehicle_tax_exempted_reason}
                    onChange={handleChange}
                    disabled={!isTaxExempt}
                    required={isTaxExempt}
                  />

                  <CategoryDropdown
                    categoryType="GST"
                    name="vehcile_gst_treatment"
                    label="GST Treatment"
                    value={formData.vehcile_gst_treatment}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehcile_gst_treatment: val,
                      }))
                    }
                    size="small"
                    fullWidth
                  />
                  <Field
                    label="GST number"
                    name="vehicle_gst_no"
                    value={formData.vehicle_gst_no}
                    onChange={handleChange}
                    disabled={!isRegistered}
                    required={isRegistered}
                  />
                  <CategoryDropdown
                    categoryType="State"
                    name="vehile_place_of_supply"
                    label="Place of Supply"
                    value={formData.vehile_place_of_supply}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehile_place_of_supply: val,
                      }))
                    }
                    size="small"
                    fullWidth
                  />

                  <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3 }}>
                    <Toggle
                      label="Parking / toll / permit"
                      name="vehicle_gst_parking_toll_permit_flg"
                      checked={formData.vehicle_gst_parking_toll_permit_flg}
                      onChange={setFlag("vehicle_gst_parking_toll_permit_flg")}
                    />
                    <Toggle
                      label="Batta"
                      name="vehicle_gst_batta_flg"
                      checked={formData.vehicle_gst_batta_flg}
                      onChange={setFlag("vehicle_gst_batta_flg")}
                    />
                    <Toggle
                      label="Others"
                      name="vehicle_gst_others_flg"
                      checked={formData.vehicle_gst_others_flg}
                      onChange={setFlag("vehicle_gst_others_flg")}
                    />
                  </Box>
                </Grid>
              </Card>
            </Box>
          </Panel>

          {/* ---------- OWNER / DRIVER ---------- */}
          <Panel active={activeTab === 1}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
                alignItems: "start",
              }}
            >
              <Card title="Driver">
                <Grid cols={1}>
                  <Field
                    label="Driver name"
                    name="vehicle_driver_name"
                    value={formData.vehicle_driver_name}
                    onChange={handleChange}
                  />
                  <Field
                    label="Driver mobile no."
                    type="tel"
                    inputProps={{ maxLength: 15 }}
                    name="vehicle_driver_mobile_no"
                    value={formData.vehicle_driver_mobile_no}
                    onChange={handleChange}
                  />
                </Grid>
              </Card>

              <Card title="Owner">
                <Grid cols={1}>
                  <Field
                    label="Owner name"
                    name="vehicle_owner_name"
                    value={formData.vehicle_owner_name}
                    onChange={handleChange}
                  />
                  <Field
                    label="Owner mobile"
                    type="tel"
                    inputProps={{ maxLength: 10 }}
                    name="vehicle_owner_mobile"
                    value={formData.vehicle_owner_mobile}
                    onChange={handleChange}
                  />
                  <Field
                    label="Owner PAN no."
                    inputProps={{ maxLength: 15 }}
                    name="vehicle_owner_pan_no"
                    value={formData.vehicle_owner_pan_no}
                    onChange={handleChange}
                  />
                  <Field
                    label="Owner name in PAN"
                    name="vehicle_owner_name_in_pan"
                    value={formData.vehicle_owner_name_in_pan}
                    onChange={handleChange}
                  />
                  <Field
                    label="Owner address"
                    multiline
                    rows={3}
                    name="vehicle_owner_address"
                    value={formData.vehicle_owner_address}
                    onChange={handleChange}
                  />
                </Grid>
              </Card>

              <Card title="Owner bank details">
                <Grid cols={1}>
                  <Field
                    label="Account no."
                    name="vehicle_owner_bank_ac_no"
                    value={formData.vehicle_owner_bank_ac_no}
                    onChange={handleChange}
                  />
                  <Field
                    label="Account name"
                    name="vehicle_owner_bank_ac_name"
                    value={formData.vehicle_owner_bank_ac_name}
                    onChange={handleChange}
                  />
                  <Field
                    label="Bank name"
                    name="vehicle_owner_bank_name"
                    value={formData.vehicle_owner_bank_name}
                    onChange={handleChange}
                  />
                  <Field
                    label="Branch"
                    name="vehicle_owner_bank_branch"
                    value={formData.vehicle_owner_bank_branch}
                    onChange={handleChange}
                  />
                  <Field
                    label="IFSC code"
                    inputProps={{
                      style: { textTransform: "uppercase" },
                      maxLength: 15,
                    }}
                    name="vehicle_owner_bank_ifsc_code"
                    value={formData.vehicle_owner_bank_ifsc_code}
                    onChange={handleChange}
                  />
                </Grid>
              </Card>
            </Box>
          </Panel>

          {/* ---------- DOCUMENTS ---------- */}
          <Panel active={activeTab === 2}>
            <DocumentManager
              entityType="vehicle"
              entityId={editData?.vehicle_id || formData.client_id}
              clientId={clientId}
              onDocumentsChange={handleDocumentsChange}
              documentCategoryType="VehicleDocument"
            />
          </Panel>

          {/* ---------- GPRS ---------- */}
          <Panel active={activeTab === 3}>
            <Card title="GPS settings" sx={{ maxWidth: 600 }}>
              <Grid cols={1}>
                <Field
                  label="GPS API URL"
                  name="gps_api_url"
                  value={formData.gps_api_url}
                  onChange={handleChange}
                />
                <Field
                  label="GPS API key"
                  name="gps_api_key"
                  value={formData.gps_api_key}
                  onChange={handleChange}
                />
                <Field
                  label="GPS device number"
                  name="gps_device_no"
                  value={formData.gps_device_no}
                  onChange={handleChange}
                />
              </Grid>
            </Card>
          </Panel>

          {/* ---------- TARIFF ---------- */}
          <Panel active={activeTab === 4}>
            <TariffManager
              entityType="vehicle"
              entityId={editData?.vehicle_id || ""}
              clientId={clientId}
              token={token}
              readOnly={false}
              onTariffsChange={handleTariffsChange}
              ref={tariffManagerRef}
            />
          </Panel>
        </Box>

        {/* ---------- Sticky action bar ---------- */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            py: 1.5,
            bgcolor: "#fff",
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={saving}
            sx={{ ...btnBase, borderColor: C.border, color: C.muted }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{
              ...btnBase,
              backgroundColor: C.primary,
              "&:hover": { backgroundColor: C.primaryHover },
            }}
          >
            {saving ? "Saving…" : editData ? "Update vehicle" : "Save vehicle"}
          </Button>
        </Box>
      </Box>
    </form>
  );
}
